import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useParams } from 'react-router-dom';
import { appPaths } from 'consts/paths/paths';
import { MealTypeAddEditForm } from 'components/templates';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { MealTypeId, MealTypesFormFields } from 'types';
import { useEditForm } from 'hooks/useEditForm/useEditForm';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { ErrorDisplay } from 'components/molecules';
import { parseMealTypesFormFieldsIntoAddUpdateMealTypeDTO } from 'utils/parsers/mealTypes/parseMealTypesFormFieldsIntoAddUpdateMealTypeDTO';
import { parseMealTypeDTOIntoMealTypesFormFields } from 'utils/parsers/mealTypes/parseMealTypeDTOIntoMealTypesFormFields';
import { settingsTexts } from 'consts/text';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { mealTypeAddEditErrorParser } from 'utils/errorHandlers/settings/mealTypeAddEditErrorParser';

export const MealTypesEditPage = () => {
    const { form, formInitialData, setFormData } = useEditForm<MealTypesFormFields>();
    const { statefulNavigate } = useNavigateWithState();
    const { displaySuccess } = useSimpleNotification();

    const { mealTypeId: mealTypeIdParam } = useParams<{ mealTypeId: MealTypeId }>();
    const mealTypeId = mealTypeIdParam || ('' as MealTypeId);

    const onSuccess = () => {
        displaySuccess(settingsTexts.mealTypes.successNotifications.mealTypeEdited);
        statefulNavigate(appPaths.app.settings.diets.mealTypes.base, { replace: true });
    };

    const { isFetching: isMealTypeDataFetching, isError: isMealTypeDataError } = useAppQuery(
        'MEAL_TYPE',
        [mealTypeId],
        () => StaffHTTPService.mealTypes.getMealType(mealTypeId),
        {
            onSuccess: (data) => setFormData(parseMealTypeDTOIntoMealTypesFormFields(data)),
            refetchOnWindowFocus: false,
        },
    );

    const { mutate, error, isLoading } = useAppMutation(StaffHTTPService.mealTypes.updateMealType, {
        key: ['UPDATE_MEAL_TYPE'],
        onSuccess,
        invalidateQueryKeys: [['MEAL_TYPES'], ['MEAL_TYPE', mealTypeId]],
    });

    const onFinish = (mealTypeData: MealTypesFormFields) => {
        const parsedUpdateMealTypeDTO =
            parseMealTypesFormFieldsIntoAddUpdateMealTypeDTO(mealTypeData);
        mutate({ mealTypeId, updateMealTypeDTO: parsedUpdateMealTypeDTO });
    };

    if (isMealTypeDataError) {
        return <ErrorDisplay />;
    }

    return (
        <MealTypeAddEditForm
            type="edit"
            axiosError={error ? mealTypeAddEditErrorParser(error) : undefined}
            isLoading={isMealTypeDataFetching || isLoading}
            onFinish={onFinish}
            form={form}
            initialValues={formInitialData}
        />
    );
};
